import {
  QueryDatasetDocument,
  useRunExportJobMutation,
} from '@warebee/frontend/data-access-api-graphql';
import {
  ActualityHqWithCongestionsParams,
  ActualityHqWithCongestionsRow,
  getActualityHqWithCongestionsDataRowsQuery,
  getActualityHqWithCongestionsTotalQuery,
} from '@warebee/shared/dataset-queries';
import {
  DatasetQueryConverterConfig,
  DatasetQueryExportJobParams,
  generateExportFilename,
} from '@warebee/shared/export-converter';
import { nanoid } from 'nanoid';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { AsyncLoadStatus } from '../../common/types';
import { errorAppender } from '../../store/error.state';
import { brandName } from '../../store/global.state';
import {
  warehouseSelected,
  warehouseSelectedId,
} from '../../store/warehouse.state';
import {
  actualityHqCongestionsTableData,
  actualityHqCongestionsTableDataLoadStatus,
  actualityHqCongestionsTableDataState,
  actualityHqCongestionsTableTotalCount,
} from '../store/actuality.hq.state';
import { executeDatasetQuery } from '../store/feed.helper';
import useActualityCongestionDataTableConfig from './useActualityCongestionDataTableConfig';

export type LoadActualityHqCongestionsTableDataParams =
  ActualityHqWithCongestionsParams & {
    isAppend?: boolean;
  };

function useLoadActualityHqCongestionsTableData() {
  const { t } = useTranslation('errors');
  const errorTitle = t`Cannot load Actuality Hq With Congestions Data`;
  const brandTitleState = useRecoilValue(brandName);
  const warehouse = useRecoilValue(warehouseSelected);
  const warehouseIdState = useRecoilValue(warehouseSelectedId);

  const columnsConfig = useActualityCongestionDataTableConfig();
  const [runExportCSV] = useRunExportJobMutation();

  const resetCallback = useRecoilCallback(({ reset }) => async () => {
    reset(actualityHqCongestionsTableDataLoadStatus);
    reset(actualityHqCongestionsTableData);
    reset(actualityHqCongestionsTableDataState);
    reset(actualityHqCongestionsTableTotalCount);
  });

  const initLoading = useRecoilCallback(
    ({ set, reset }) =>
      async (params: LoadActualityHqCongestionsTableDataParams) => {
        set(actualityHqCongestionsTableDataLoadStatus, AsyncLoadStatus.Loading);
        if (!params.isAppend) {
          reset(actualityHqCongestionsTableData);
          reset(actualityHqCongestionsTableTotalCount);
        }
      },
  );

  const loadDataCallback = useRecoilCallback(
    ({ snapshot, set }) =>
      async (params: LoadActualityHqCongestionsTableDataParams) => {
        const warehouseId = await snapshot.getPromise(warehouseSelectedId);
        const current = await snapshot.getPromise(
          actualityHqCongestionsTableData,
        );
        let totalCount = await snapshot.getPromise(
          actualityHqCongestionsTableTotalCount,
        );

        function handleError(details, stack) {
          set(errorAppender, {
            id: nanoid(),
            title: errorTitle,
            details: details,
            callStack: stack,
          });
          set(actualityHqCongestionsTableDataLoadStatus, AsyncLoadStatus.Error);
        }

        if (!params.isAppend) {
          const compiledQueryTotal = getActualityHqWithCongestionsTotalQuery({
            ...params,
            congestedOnly: true,
          }).compile();

          executeDatasetQuery({
            warehouseId,
            compiledQuery: compiledQueryTotal,
            comment: '[actuality] Hq With Congestions total rows',
          }).then(([totalResult]) => {
            totalCount =
              totalResult && totalResult.length > 0
                ? totalResult[0].totalCount
                : 0;
            set(actualityHqCongestionsTableTotalCount, totalCount);
          });
        }

        let rows = [];
        const compiledQuery =
          getActualityHqWithCongestionsDataRowsQuery(params).compile();

        [rows] = await executeDatasetQuery({
          warehouseId,
          compiledQuery,
          comment: '[actuality] Hq With Congestions',
        });

        if (params.isAppend) {
          rows = [...current, ...rows];
        }
        set(actualityHqCongestionsTableData, rows);
        set(actualityHqCongestionsTableDataLoadStatus, AsyncLoadStatus.Ok);
      },
  );

  const exportCallback = useRecoilCallback(
    ({ snapshot }) =>
      async (params: ActualityHqWithCongestionsParams) => {
        const warehouseId = await snapshot.getPromise(warehouseSelectedId);
        const exportParams: ActualityHqWithCongestionsParams = {
          ...params,
          congestedOnly: true,
          page: null,
        };
        const query =
          getActualityHqWithCongestionsDataRowsQuery(exportParams).compile();
        const converterConfig: DatasetQueryConverterConfig<ActualityHqWithCongestionsRow> =
          {
            columnsConfig,
          };

        const filename = generateExportFilename({
          exportTitle: t`actuality-congestions`,
          brandTitleState,
          warehouse,
          warehouseIdState,
        });

        const variables: DatasetQueryExportJobParams<ActualityHqWithCongestionsRow> =
          {
            converterId: 'DATASET_QUERY',
            query: QueryDatasetDocument.loc.source.body,
            config: converterConfig,
            variables: {
              warehouseId: warehouseId,
              query: query.sql,
              params: query.parameters,
            },
            filename: `${filename}.csv`,
          };

        const { data, errors } = await runExportCSV({
          variables,
        });

        return {
          errors: errors,
          job: data.createExportJob,
        };
      },
  );

  async function call(params: LoadActualityHqCongestionsTableDataParams) {
    await initLoading(params);
    await loadDataCallback(params);
  }

  async function reset() {
    await resetCallback();
  }

  async function exportCSV(params: LoadActualityHqCongestionsTableDataParams) {
    return await exportCallback(params);
  }

  return [call, reset, exportCSV] as const;
}

export default useLoadActualityHqCongestionsTableData;
